import type { IntlShape } from 'react-intl';

import type { MinMax } from '../../../../core/types/min-max.js';
import type { SeriesActionsTemplate } from '../../../../core/types/series-actions-template.js';
import { safeGet } from '../../../../core/utils/safe-get.js';
import type { SeriesIdToColorScale } from '../../../context/XYChartColorScales.context.js';
import type { FormatCategory } from '../../../hooks/useFormatCategory.js';
import { isCategorical } from '../../../types/guards.js';
import type { TooltipContent } from '../../../types/state.js';
import {
  AREA_VARIANT,
  BAR_VARIANT,
  DOT_VARIANT,
  LINE_VARIANT,
  RECT_VARIANT,
  type AreaMetadata,
  type AreaSeriesToMetadata,
  type AreaSeriesWithDatapoints,
  type AxisIdToFormatter,
  type AxisIdToScaleDomain,
  type BarMetadata,
  type BarSeriesToMetadata,
  type BarSeriesWithDatapoints,
  type BaseMetadata,
  type DotMetadata,
  type LineMetadata,
  type MapDatapoints,
  type RectMetadata,
  type RectSeriesToMetadata,
  type SeriesToMetadata,
} from '../../../types/xy-chart-internals.js';
import type { RectDatapoint } from '../../../types/xy-chart.js';
import {
  buildAreaSeriesActionItem,
  buildBarSeriesActionItem,
} from '../../../utils/build-series-action-item.js';
import {
  getRelativeAccessor,
  toRelative,
} from '../../../utils/value-representation.js';
import { buildClosestPointTooltipBodyProps } from '../ClosestPointTooltip/closestPointTooltipBodyUtils.js';
import { getClosestPointTooltipBodyContent } from '../ClosestPointTooltip/hooks/getClosestPointTooltipBodyContent.js';
import { getHoveredPointTooltipBodyContent } from '../HoveredPointTooltip/hooks/getHoveredPointTooltipBodyContent.js';
import { buildHoveredPointTooltipBodyProps } from '../HoveredPointTooltip/hoveredPointTooltipBodyUtils.js';
import { getColor } from '../utils/get-color.js';
import { getTooltipFormatter } from '../utils/get-tooltip-formatter.js';
import type { XYChartTooltipBodyProps } from '../XYChartTooltipBody/XYChartTooltipBody.js';

type SharedTooltipContext = {
  seriesToMetadata: SeriesToMetadata;
  axisIdToFormatter: AxisIdToFormatter;
  axisIdToScaleDomain: AxisIdToScaleDomain;
  datapointMetadataMap: MapDatapoints;
  formatCategory: FormatCategory;
  intl: IntlShape;
  allBarSeries: BarSeriesWithDatapoints[];
  allAreaSeries: AreaSeriesWithDatapoints[];
};

export type SharedTooltipRow = {
  name: string;
  nameSegments?: string[];
  color: string;
  yAxisValue: string;
  yAxisDuration: string | undefined;
  actions?: () => SeriesActionsTemplate;
};

export type SharedTooltipHeader = {
  xAxisValue: string;
  xAxisDuration: string | undefined;
  label: string | undefined;
};

/**
 * Bar/Rect have explicit hovered geometry, so they format via the
 * hovered-point helper. Dot/Line/Area pick the closest sample under
 * the cursor and format via the closest-point helper. Both helpers
 * return the same `XYChartTooltipBodyProps` shape.
 */
function buildTooltipBody(
  content: TooltipContent,
  ctx: SharedTooltipContext,
): XYChartTooltipBodyProps {
  const isHoveredShape =
    content.type === RECT_VARIANT || content.type === BAR_VARIANT;

  if (isHoveredShape) {
    return buildHoveredPointTooltipBodyProps({
      datapoint: content.datapoint,
      series: content.series,
      seriesToMetadata: ctx.seriesToMetadata as unknown as RectSeriesToMetadata,
      axisIdToFormatter: ctx.axisIdToFormatter,
      axisIdToScaleDomain: ctx.axisIdToScaleDomain,
      datapointMetadataMap: ctx.datapointMetadataMap,
      formatCategory: ctx.formatCategory,
      intl: ctx.intl,
    });
  }

  return buildClosestPointTooltipBodyProps({
    datapoint: content.datapoint,
    series: content.series,
    seriesToMetadata: ctx.seriesToMetadata as never,
    axisIdToFormatter: ctx.axisIdToFormatter,
    axisIdToScaleDomain: ctx.axisIdToScaleDomain,
    datapointMetadataMap: ctx.datapointMetadataMap,
    intl: ctx.intl,
  });
}

export function buildSharedTooltipHeader(
  preferred: TooltipContent,
  ctx: SharedTooltipContext,
): SharedTooltipHeader {
  const { xAxisValue, xAxisDuration } = buildTooltipBody(preferred, ctx);
  return {
    xAxisValue,
    xAxisDuration,
    label: preferred.datapoint.x0 instanceof Date ? '' : undefined,
  };
}

// Computes only the y-axis formatted value for a BAR/RECT row, reusing the
// already-retrieved metadata to avoid a second seriesToMetadata lookup and
// skipping x-axis formatting that is unused in shared tooltip rows.
function buildRectYAxisValue(
  result: Extract<
    TooltipContent,
    { type: typeof BAR_VARIANT | typeof RECT_VARIANT }
  >,
  metadata: BaseMetadata,
  ctx: SharedTooltipContext,
): { yAxisValue: string; yAxisDuration: string | undefined } {
  const {
    axisIdToFormatter,
    axisIdToScaleDomain,
    datapointMetadataMap,
    formatCategory,
    intl,
  } = ctx;
  const { yAxis } = metadata;

  const yResolvedFormatter = isCategorical(yAxis.type)
    ? (value: string) => formatCategory(yAxis, value) ?? value
    : getTooltipFormatter(yAxis, axisIdToFormatter, intl);

  const datapointMetadata = datapointMetadataMap.get(result.datapoint);
  const binDomain = datapointMetadata?.binDomain;
  const isRelative = typeof binDomain === 'number' && binDomain;

  // Mirrors the `xDomain` variable in buildHoveredPointTooltipBodyProps —
  // confusingly named there, it is the domain fed into the y-axis relative accessor.
  const yRelativeDomain: MinMax =
    isRelative && datapointMetadata?.baseline === 'x'
      ? { min: binDomain * -1, max: binDomain }
      : (safeGet(axisIdToScaleDomain, yAxis.id) as MinMax);

  const { y0, y1 } = result.datapoint;
  const { text: yAxisValue, duration: yAxisDuration } =
    getHoveredPointTooltipBodyContent(
      intl,
      y0,
      y1,
      yResolvedFormatter,
      getRelativeAccessor(yAxis, yRelativeDomain),
    );

  return { yAxisValue, yAxisDuration };
}

function buildLinearYAxisValue(
  result: TooltipContent,
  metadata: BaseMetadata,
  ctx: SharedTooltipContext,
): { yAxisValue: string; yAxisDuration: string | undefined } {
  const { axisIdToFormatter, axisIdToScaleDomain, datapointMetadataMap, intl } =
    ctx;
  const { yAxis } = metadata;

  const yFormatter = getTooltipFormatter(yAxis, axisIdToFormatter, intl);

  const datapointMetadata = datapointMetadataMap.get(result.datapoint);
  const binDomain = datapointMetadata?.binDomain;
  let yDomain = safeGet(axisIdToScaleDomain, yAxis.id) as MinMax;

  if (result.series.type === AREA_VARIANT) {
    const isStacked = (metadata as AreaMetadata).isStacked;
    if (isStacked && datapointMetadata?.baseline !== 'y' && binDomain) {
      yDomain = { min: binDomain * -1, max: binDomain };
    }
  }

  const { text: yAxisValue, duration: yAxisDuration } =
    getClosestPointTooltipBodyContent(
      [result.datapoint.y0, result.datapoint.y1],
      yFormatter,
      yAxis.isRelative ? (value) => toRelative(value, yDomain) : undefined,
    );

  return { yAxisValue, yAxisDuration };
}

function buildFlatActions<T extends { name: string }>(
  metadataActions: ((item: T) => SeriesActionsTemplate) | undefined,
  datapoint: Omit<T, 'name'>,
  name: string,
): (() => SeriesActionsTemplate) | undefined {
  return metadataActions
    ? () => metadataActions({ ...datapoint, name } as T)
    : undefined;
}

export function buildSharedTooltipRow(
  result: TooltipContent,
  ctx: SharedTooltipContext & { colorScaleMap: SeriesIdToColorScale },
): SharedTooltipRow {
  const {
    seriesToMetadata,
    datapointMetadataMap,
    colorScaleMap,
    allBarSeries,
    allAreaSeries,
  } = ctx;
  const metadata = safeGet(seriesToMetadata, result.series);
  const colorOverride =
    result.type === RECT_VARIANT || result.type === BAR_VARIANT
      ? safeGet(datapointMetadataMap, result.datapoint).colorOverride
      : undefined;

  // `getColor` returns the rect color from the color scale when applicable
  // and otherwise falls back to `metadata.color.Default`. The cast is safe
  // at runtime: `AreaMetadata` is structurally compatible.
  const color =
    getColor(
      result.series,
      result.datapoint,
      metadata as RectMetadata | BarMetadata | DotMetadata | LineMetadata,
      colorScaleMap,
      colorOverride,
    ) ?? '';

  const { yAxisValue, yAxisDuration } =
    result.type === BAR_VARIANT || result.type === RECT_VARIANT
      ? buildRectYAxisValue(result, metadata, ctx)
      : buildLinearYAxisValue(result, metadata, ctx);

  let actions: (() => SeriesActionsTemplate) | undefined;
  if (result.type === BAR_VARIANT) {
    const metadataActions = (metadata as BarMetadata).actions;
    if (metadataActions) {
      const { datapoint, series } = result;
      actions = () => {
        const actionItem = buildBarSeriesActionItem({
          datapoint,
          allSeries: allBarSeries,
          seriesToMetadata: seriesToMetadata as unknown as BarSeriesToMetadata,
          datapointMetadataMap,
          currentSeries: series,
        });
        return metadataActions(actionItem);
      };
    }
  } else if (result.type === AREA_VARIANT) {
    const metadataActions = (metadata as AreaMetadata).actions;
    if (metadataActions) {
      const { datapoint, series } = result;
      actions = () => {
        const actionItem = buildAreaSeriesActionItem({
          datapoint,
          allSeries: allAreaSeries,
          seriesToMetadata: seriesToMetadata as unknown as AreaSeriesToMetadata,
          datapointMetadataMap,
          currentSeries: series,
        });
        return metadataActions(actionItem);
      };
    }
  } else if (result.type === RECT_VARIANT) {
    const metadataActions = (metadata as RectMetadata).actions;
    actions = metadataActions
      ? () => metadataActions(result.datapoint as unknown as RectDatapoint)
      : undefined;
  } else if (result.type === LINE_VARIANT) {
    actions = buildFlatActions(
      (metadata as LineMetadata).actions,
      result.datapoint,
      metadata.name,
    );
  } else if (result.type === DOT_VARIANT) {
    actions = buildFlatActions(
      (metadata as DotMetadata).actions,
      result.datapoint,
      metadata.name,
    );
  }

  return {
    name: metadata.name,
    nameSegments: metadata.nameSegments,
    color,
    yAxisValue,
    yAxisDuration,
    actions,
  };
}
